import React, { useState, useEffect, useCallback } from 'react';
import { satisfaction as api } from '../services/api';
import SatisfactionFormulaireEditor from './SatisfactionFormulaireEditor';
import SatisfactionReponseModal from './SatisfactionReponseModal';
import SatisfactionNouveauModal from './SatisfactionNouveauModal';
import SatisfactionTableauBord from './SatisfactionTableauBord';
import './Satisfaction.css';

function fmtDate(d) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return '—'; }
}

const MODE_BADGE = {
  off: { label: 'Off', classe: 'status-info' },
  simulation: { label: 'Observation', classe: 'status-warning' },
  actif: { label: 'Actif', classe: 'status-success' },
};

function NpsBadge({ score }) {
  if (score === null || score === undefined) return <span className="sat-vide-inline">—</span>;
  const n = Number(score);
  const cls = n >= 9 ? 'status-success' : n >= 7 ? 'status-warning' : 'status-error';
  return <span className={cls}>{n}/10</span>;
}

// ─── Onglet Réponses ─────────────────────────────────────────────────────────
function OngletReponses({ user, editMode, formulaires, onOuvrir }) {
  const [envois, setEnvois] = useState([]);
  const [stats, setStats] = useState(null);
  const [filtre, setFiltre] = useState('');
  const [traitement, setTraitement] = useState('');
  const [formulaireId, setFormulaireId] = useState('');
  const [site, setSite] = useState('');
  const [sites, setSites] = useState([]);
  const [selection, setSelection] = useState([]);
  const [loading, setLoading] = useState(true);
  const isSuperAdmin = user?.role === 'super-admin';

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      // « simulation » n'est pas un état de traitement mais un autre périmètre :
      // ce que le planificateur AURAIT envoyé. D'où un paramètre distinct.
      if (traitement === 'simulation') params.mode = 'simulation';
      else if (traitement) params.traitement = traitement;
      if (formulaireId) params.formulaire_id = formulaireId;
      if (site) params.site = site;
      if (filtre) params.search = filtre;
      const [{ data: e }, { data: s }] = await Promise.all([api.getEnvois(params), api.getStats(params)]);
      setEnvois(e.envois || []);
      setStats(s);
    } finally {
      setLoading(false);
    }
  }, [traitement, formulaireId, filtre, site]);

  useEffect(() => { charger(); }, [charger]);
  // Les points de vente ne dépendent pas des filtres : on les charge une fois.
  useEffect(() => { api.getSites().then(({ data }) => setSites(data.sites || [])).catch(() => {}); }, []);

  const supprimerSelection = async () => {
    if (!window.confirm(`Supprimer ${selection.length} réponse(s) ? Cette action est définitive.`)) return;
    await api.deleteEnvois(selection);
    setSelection([]);
    charger();
  };

  const enSimulation = traitement === 'simulation';

  return (
    <>
      {enSimulation && (
        <div className="status-warning sat-bandeau">
          👁️ <b>Phase d'observation.</b> Ces lignes sont ce que le planificateur <b>aurait envoyé</b> :
          aucun message n'est parti, aucun de ces clients n'a été contacté, et aucune réponse ne viendra.
        </div>
      )}
      {stats && !enSimulation && (
        <div className="sat-kpis">
          <div className="sat-kpi"><span>{stats.total_envoyes}</span>Envoyés</div>
          <div className="sat-kpi"><span>{stats.total_repondus}</span>Réponses</div>
          <div className="sat-kpi"><span>{stats.nps_moyen ?? '—'}</span>NPS moyen</div>
          <div className="sat-kpi"><span>{stats.note_moyenne ?? '—'}</span>Note /5</div>
          <div className="sat-kpi sat-kpi-alerte"><span>{stats.detracteurs}</span>Détracteurs</div>
          <div className="sat-kpi sat-kpi-alerte"><span>{stats.a_traiter}</span>À traiter</div>
          {stats.total_echecs > 0 && (
            <div className="sat-kpi sat-kpi-alerte"><span>{stats.total_echecs}</span>Échecs d'envoi</div>
          )}
        </div>
      )}

      <div className="page-toolbar">
        <input className="page-search" placeholder="🔍 Rechercher..." value={filtre}
          onChange={(e) => setFiltre(e.target.value)} />
        <select className={`page-select${traitement ? ' active-filter' : ''}`} value={traitement}
          onChange={(e) => setTraitement(e.target.value)}>
          <option value="">Tous les états</option>
          <option value="a_traiter">À traiter</option>
          <option value="detracteurs">Détracteurs (NPS ≤ 6)</option>
          <option value="traite">Déjà traités</option>
          <option value="echec">⚠️ Échecs d'envoi</option>
          <option value="simulation">👁️ Observation (rien n'est parti)</option>
        </select>
        <select className={`page-select${formulaireId ? ' active-filter' : ''}`} value={formulaireId}
          onChange={(e) => setFormulaireId(e.target.value)}>
          <option value="">Tous les formulaires</option>
          {formulaires.map((f) => <option key={f.id} value={f.id}>{f.nom} — {f.concession_nom}</option>)}
        </select>
        {sites.length > 1 && (
          <select className={`page-select${site ? ' active-filter' : ''}`} value={site}
            onChange={(e) => setSite(e.target.value)}>
            <option value="">Tous les points de vente</option>
            {sites.map((x) => (
              <option key={x.site_code} value={x.site_code}>
                {x.site_nom || x.site_code} ({x.reponses}/{x.envois})
              </option>
            ))}
          </select>
        )}
        {editMode && isSuperAdmin && selection.length > 0 && (
          <button className="btn-secondary" onClick={supprimerSelection}>
            🗑️ Supprimer la sélection ({selection.length})
          </button>
        )}
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              {editMode && isSuperAdmin && <th style={{ width: 36 }}></th>}
              <th>Client</th>
              <th>Point de vente</th>
              <th>Formulaire</th>
              <th>Envoyé</th>
              <th>Répondu</th>
              <th>NPS</th>
              <th>Verbatim</th>
              <th>Traitement</th>
            </tr>
          </thead>
          <tbody>
            {envois.map((e) => (
              <tr key={e.id} onClick={enSimulation ? undefined : () => onOuvrir(e.id)}
                  className={enSimulation ? undefined : 'sat-ligne-cliquable'}>
                {editMode && isSuperAdmin && (
                  <td onClick={(ev) => ev.stopPropagation()}>
                    <input type="checkbox" checked={selection.includes(e.id)}
                      onChange={(ev) => setSelection(ev.target.checked
                        ? [...selection, e.id] : selection.filter((x) => x !== e.id))} />
                  </td>
                )}
                <td>{[e.client_prenom, e.client_nom].filter(Boolean).join(' ') || '—'}</td>
                <td className={e.site_nom || e.site_code ? '' : 'sat-vide-inline'}>
                  {e.site_nom || e.site_code || '—'}
                </td>
                <td>{e.formulaire_nom}</td>
                <td>{fmtDate(e.envoye_le)}</td>
                <td>{enSimulation
                  ? <span className="sat-vide-inline">—</span>
                  : e.statut === 'echec'
                    ? <span className="status-error" title={e.erreur || ''}>Jamais parti</span>
                    : e.statut === 'repondu' ? fmtDate(e.repondu_le) : <span className="sat-vide-inline">En attente</span>}</td>
                <td><NpsBadge score={e.nps} /></td>
                <td className="sat-cellule-verbatim">{e.verbatim || '—'}</td>
                <td>
                  {enSimulation ? <span className="status-info">Observation</span>
                    : e.traite_le ? <span className="status-success">✓ {fmtDate(e.traite_le)}</span>
                    : e.statut === 'repondu' ? <span className="status-warning">À traiter</span> : '—'}
                </td>
              </tr>
            ))}
            {!loading && envois.length === 0 && (
              <tr><td colSpan={9} className="sat-vide">
                {enSimulation
                  ? 'Aucune ligne d\'observation. Le planificateur en consigne une par destinataire, chaque jour, pour un formulaire en mode « observation ».'
                  : 'Aucune réponse pour ces filtres.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ─── Onglet Formulaires ──────────────────────────────────────────────────────
function OngletFormulaires({ user, formulaires, catalogue, onRecharger, onEditer }) {
  const libelleSource = (cle) => (catalogue?.sources || []).find((s) => s.cle === cle)?.label || cle;
  const isSuperAdmin = user?.role === 'super-admin';

  const dupliquer = async (id) => { await api.dupliquer(id); onRecharger(); };
  const supprimer = async (f) => {
    if (!window.confirm(`Supprimer le formulaire « ${f.nom} » ?`)) return;
    try {
      await api.deleteFormulaire(f.id);
      onRecharger();
    } catch (e) {
      window.alert(e.response?.data?.error || 'Suppression impossible');
    }
  };

  return (
    <div className="table-container">
      <table>
        <thead>
          <tr>
            <th>Formulaire</th>
            <th>Concession</th>
            <th>Questions</th>
            <th>Source</th>
            <th>Fréquence</th>
            <th>Mode</th>
            <th>Envois</th>
            <th>Réponses</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {formulaires.map((f) => {
            const badge = MODE_BADGE[f.mode] || MODE_BADGE.off;
            return (
              <tr key={f.id}>
                <td>
                  {f.nom}
                  {!f.actif && <span className="status-info sat-pastille">inactif</span>}
                </td>
                <td>{f.concession_nom}</td>
                <td>{f.nb_questions}</td>
                <td>{libelleSource(f.source)}</td>
                <td>{String(f.heure_envoi || '').slice(0, 5)} · {f.timezone?.split('/')[1]}</td>
                <td><span className={badge.classe}>{badge.label}</span></td>
                <td>{f.nb_envois}</td>
                <td>{f.nb_reponses}</td>
                <td>
                  <div className="action-buttons">
                    <button className="btn-icon" title="Modifier" onClick={() => onEditer(f.id)}>✏️</button>
                    <button className="btn-icon" title="Dupliquer" onClick={() => dupliquer(f.id)}>📄</button>
                    {isSuperAdmin && (
                      <button className="btn-icon delete" title="Supprimer" onClick={() => supprimer(f)}>🗑️</button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
          {formulaires.length === 0 && (
            <tr><td colSpan={9} className="sat-vide">Aucun formulaire. Créez-en un pour commencer.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────
export default function Satisfaction({ user, concessions, setHeaderAction, editMode }) {
  // On arrive sur le tableau de bord : la première question est « où en
  // sommes-nous ? », pas « quelle est la 1 400e réponse ? ».
  const [onglet, setOnglet] = useState('reponses');
  const [formulaires, setFormulaires] = useState([]);
  const [catalogue, setCatalogue] = useState(null);
  const [editionId, setEditionId] = useState(null);
  const [nouveau, setNouveau] = useState(false);
  const [reponseId, setReponseId] = useState(null);
  const [rafraichir, setRafraichir] = useState(0);

  const chargerFormulaires = useCallback(async () => {
    const { data } = await api.getFormulaires();
    setFormulaires(data.formulaires || []);
  }, []);

  useEffect(() => { chargerFormulaires(); }, [chargerFormulaires, rafraichir]);
  useEffect(() => { api.getCatalogue().then(({ data }) => setCatalogue(data)).catch(() => {}); }, []);

  // La création passe par une modale et non par des `window.prompt` : elle doit
  // faire choisir la SOURCE des destinataires, qui commande tout le reste du
  // formulaire (délai, quota, variables du message).
  const creer = useCallback(async ({ nom, concession_id, source }) => {
    const conc = user?.concession_id || concession_id
      || (concessions?.length === 1 ? concessions[0].id : null);
    const { data } = await api.createFormulaire({ nom, concession_id: conc, source });
    setNouveau(false);
    setEditionId(data.formulaire.id);
    setRafraichir((n) => n + 1);
  }, [user, concessions]);

  useEffect(() => {
    if (setHeaderAction) {
      setHeaderAction({ primary: { label: '+ Nouveau formulaire', onClick: () => setNouveau(true) } });
    }
  }, [setHeaderAction]);

  return (
    <div className="page-container">
      <div className="page-tabs">
        <button className={onglet === 'reponses' ? 'active' : ''} onClick={() => setOnglet('reponses')}>
          📥 Réponses
        </button>
        <button className={onglet === 'bord' ? 'active' : ''} onClick={() => setOnglet('bord')}>
          📊 Tableau de bord
        </button>
        <button className={onglet === 'formulaires' ? 'active' : ''} onClick={() => setOnglet('formulaires')}>
          ⚙️ Formulaires
        </button>
      </div>

      {onglet === 'bord' && <SatisfactionTableauBord formulaires={formulaires} />}
      {onglet === 'reponses' && (
        <OngletReponses key={rafraichir} user={user} editMode={editMode}
          formulaires={formulaires} onOuvrir={setReponseId} />
      )}
      {onglet === 'formulaires' && (
        <OngletFormulaires user={user} formulaires={formulaires} catalogue={catalogue}
          onRecharger={() => setRafraichir((n) => n + 1)} onEditer={setEditionId} />
      )}

      {nouveau && (
        <SatisfactionNouveauModal
          user={user} concessions={concessions} catalogue={catalogue}
          onClose={() => setNouveau(false)} onCreer={creer}
        />
      )}
      {editionId && (
        <SatisfactionFormulaireEditor
          formulaireId={editionId} catalogue={catalogue} user={user}
          onClose={() => setEditionId(null)}
          onSaved={() => setRafraichir((n) => n + 1)}
        />
      )}
      {reponseId && (
        <SatisfactionReponseModal
          envoiId={reponseId} user={user}
          onClose={() => setReponseId(null)}
          onChanged={() => setRafraichir((n) => n + 1)}
        />
      )}
    </div>
  );
}
